"use client";

import type { IconName } from "@/components/ui/icon";
import type { FilterValue, VariantFilterValue } from "@/lib/api/types";
import type { VariantField } from "@/lib/search/entity-variants";
import { fillItemScopes, type FilterScope } from "@/features/search/lib/filter-scope";
import { FilterScopedValueInput } from "@/features/search/components/filters/input/scoped-value-input";
import { BulkModifyDropdown, ClearChipsButton, FilterLabel } from "../shared";

/**
 * Every write pins each item's effective scope, so the payload always carries
 * per-item `scope` and never relies on the section-level legacy fallback.
 */
function write(
  next: FilterValue[],
  fallbackScope: FilterScope,
  onChange: (next: FilterValue[] | undefined) => void,
) {
  onChange(next.length > 0 ? fillItemScopes(next, fallbackScope) : undefined);
}

export function ScopedListHeader({
  label,
  items,
  fallbackScope = "cp",
  onChange,
}: {
  label: string;
  items: FilterValue[];
  fallbackScope?: FilterScope;
  onChange: (next: FilterValue[] | undefined) => void;
}) {
  const apply = (fn: (item: FilterValue) => FilterValue) =>
    write(items.map(fn), fallbackScope, onChange);

  return (
    <div className="flex items-center justify-between w-full">
      <FilterLabel>{label}</FilterLabel>
      {items.length > 0 && (
        <BulkModifyDropdown
          onSetScope={(scope) => apply((item) => ({ ...item, scope }))}
          onExcludeAll={() => apply((item) => ({ ...item, mode: "exclude" }))}
          onUnexcludeAll={() => apply((item) => ({ ...item, mode: "include" }))}
          onRequireAll={() => apply((item) => ({ ...item, required: true }))}
          onUnrequireAll={() => apply((item) => ({ ...item, required: false }))}
        />
      )}
    </div>
  );
}

export function ScopedValueList({
  items,
  fallbackScope = "cp",
  getLabel = (item) => item.text ?? "",
  getIcon,
  variantsField,
  onChange,
}: {
  items: VariantFilterValue[];
  fallbackScope?: FilterScope;
  getLabel?: (item: FilterValue) => string;
  getIcon?: (item: FilterValue) => IconName | undefined;
  /** Opt-in: lets each chip carry alias variants matched from this field. */
  variantsField?: VariantField;
  onChange: (next: VariantFilterValue[] | undefined) => void;
}) {
  if (items.length === 0) return null;

  return (
    <>
      {items.map((item, i) => (
        <FilterScopedValueInput
          key={`${item.text}-${i}`}
          value={item}
          label={getLabel(item)}
          icon={getIcon?.(item)}
          fallbackScope={fallbackScope}
          variantsField={variantsField}
          onChange={(updated) => {
            const next = [...items];
            next[i] = updated;
            write(next, fallbackScope, onChange);
          }}
          onRemove={() => write(items.filter((_, j) => j !== i), fallbackScope, onChange)}
        />
      ))}
      {items.length > 1 && (
        <div className="flex justify-end">
          <ClearChipsButton onClick={() => onChange(undefined)} />
        </div>
      )}
    </>
  );
}

/** Adds `text` with an explicit `"cp"` scope, or removes it if already present. */
export function toggleScopedValue(
  items: FilterValue[],
  text: string,
  fallbackScope: FilterScope = "cp",
): FilterValue[] {
  const idx = items.findIndex((item) => item.text === text);
  const next =
    idx >= 0
      ? items.filter((_, i) => i !== idx)
      : [...items, { text, required: false, scope: "cp" as const }];
  return fillItemScopes(next, fallbackScope);
}
